import React, { useMemo, useState } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';

import { ReportingPeriod, ReportingSettings, ReportingSettingsOverrides, RiskStatistic } from '@ndvr/reporting-api';

import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/utils/cn';

import {
    ADVISORY_FEES_SETTING,
    applyReportingSettingsOverrides,
    clearSettingOverride,
    getSettingVisibility,
    isSettingOverridden,
    REPORTING_CHANNELS,
    REPORTING_PERIOD_LABELS,
    REPORTING_SETTING_GROUPS,
    ReportingSettingDefinition,
    RISK_STATISTIC_LABELS,
    setEnabledPeriodsOverride,
    setRiskStatisticsOverride,
    setVisibilityOverride,
} from './reporting-settings-state';

type ReportingSettingsEditorProps = {
    baseSettings: ReportingSettings;
    overrides: ReportingSettingsOverrides;
    disabled?: boolean;
    onOverridesChange: (overrides: ReportingSettingsOverrides) => void;
};

export const ReportingSettingsEditor = ({
    baseSettings,
    overrides,
    disabled = false,
    onOverridesChange,
}: ReportingSettingsEditorProps) => {
    const effectiveSettings = useMemo(
        () => applyReportingSettingsOverrides(baseSettings, overrides),
        [baseSettings, overrides],
    );

    return (
        <div className="flex flex-col gap-6">
            <SettingsGroupCard
                settings={[ADVISORY_FEES_SETTING]}
                effectiveSettings={effectiveSettings}
                overrides={overrides}
                disabled={disabled}
                onOverridesChange={onOverridesChange}
            />

            {REPORTING_SETTING_GROUPS.map((group) => (
                <SettingsGroupCard
                    key={group.id}
                    title={group.title}
                    description={group.description}
                    settings={group.settings}
                    effectiveSettings={effectiveSettings}
                    overrides={overrides}
                    disabled={disabled}
                    onOverridesChange={onOverridesChange}
                />
            ))}

            <section className="border-cocoa-100 overflow-hidden rounded-xl border bg-white shadow-sm">
                <div className="border-cocoa-100 border-b px-7 py-5">
                    <h2 className="text-cocoa-1000 text-lg font-semibold">Transactions</h2>
                    <p className="text-cocoa-500 mt-1 text-sm">Cash flow and activity reporting</p>
                </div>
                <p className="text-cocoa-400 px-7 py-6 text-sm italic">
                    No additional reporting settings for Transactions.
                </p>
            </section>
        </div>
    );
};

type SettingsGroupCardProps = Pick<ReportingSettingsEditorProps, 'overrides' | 'disabled' | 'onOverridesChange'> & {
    title?: string;
    description?: string;
    settings: ReportingSettingDefinition[];
    effectiveSettings: ReportingSettings;
};

const SettingsGroupCard = ({
    title,
    description,
    settings,
    effectiveSettings,
    overrides,
    disabled,
    onOverridesChange,
}: SettingsGroupCardProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const content = (
        <div className="overflow-x-auto">
            <div className="min-w-[680px]">
                <ChannelColumnHeaders />
                {settings.map((setting) => (
                    <SettingRows
                        key={setting.id}
                        setting={setting}
                        effectiveSettings={effectiveSettings}
                        overrides={overrides}
                        disabled={disabled}
                        onOverridesChange={onOverridesChange}
                    />
                ))}
            </div>
        </div>
    );

    if (!title) {
        return (
            <section className="border-cocoa-100 overflow-hidden rounded-xl border bg-white shadow-sm">
                {content}
            </section>
        );
    }

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
            <section className="border-cocoa-100 overflow-hidden rounded-xl border bg-white shadow-sm">
                <CollapsibleTrigger
                    className="border-cocoa-100 flex w-full cursor-pointer items-center gap-4 border-b px-7 py-5 text-left disabled:cursor-not-allowed"
                    disabled={disabled}
                >
                    {isOpen ? (
                        <LuChevronDown className="text-cocoa-400 size-4 shrink-0" />
                    ) : (
                        <LuChevronRight className="text-cocoa-400 size-4 shrink-0" />
                    )}
                    <span className="min-w-0">
                        <span className="text-cocoa-1000 block text-lg font-semibold">{title}</span>
                        {description && <span className="text-cocoa-500 mt-1 block text-sm">{description}</span>}
                    </span>
                </CollapsibleTrigger>
                <CollapsibleContent>{content}</CollapsibleContent>
            </section>
        </Collapsible>
    );
};

const ChannelColumnHeaders = () => (
    <div className="text-cocoa-400 grid grid-cols-[minmax(0,1fr)_repeat(3,4.75rem)] items-center gap-2 px-7 py-3 text-center text-[11px] font-semibold tracking-[0.12em] uppercase">
        <span />
        {REPORTING_CHANNELS.map((channel) => (
            <span key={channel.key}>{channel.label}</span>
        ))}
    </div>
);

const SettingRows = ({
    setting,
    effectiveSettings,
    overrides,
    disabled,
    onOverridesChange,
}: Omit<SettingsGroupCardProps, 'title' | 'description' | 'settings'> & {
    setting: ReportingSettingDefinition;
}) => {
    const visibility = getSettingVisibility(effectiveSettings, setting.id);
    const anyChannelVisible = visibility.portalVisible || visibility.reportsVisible || visibility.hubVisible;
    const selectedCount =
        setting.selection === 'periods'
            ? effectiveSettings.performance.trailingReturns.enabledPeriods.length
            : setting.selection === 'statistics'
              ? effectiveSettings.performance.riskStatistics.statistics.length
              : undefined;
    const overridden = isSettingOverridden(overrides, setting.id);

    return (
        <div className="border-cocoa-100 border-t first:border-t-0">
            <div className="grid min-h-20 grid-cols-[minmax(0,1fr)_repeat(3,4.75rem)] items-center gap-2 px-7 py-4">
                <div className="min-w-0 pr-4">
                    <p className="text-cocoa-1000 text-[15px] font-medium">{setting.label}</p>
                    {setting.description && <p className="text-cocoa-400 mt-1 text-sm">{setting.description}</p>}
                    {overridden && (
                        <button
                            type="button"
                            disabled={disabled}
                            className="text-seafoam-600 hover:text-seafoam-700 mt-1 cursor-pointer text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => onOverridesChange(clearSettingOverride(overrides, setting.id))}
                        >
                            Overridden — reset to inherited
                        </button>
                    )}
                </div>
                {REPORTING_CHANNELS.map((channel) => (
                    <div key={channel.key} className="flex justify-center">
                        <Switch
                            checked={visibility[channel.key]}
                            disabled={disabled}
                            aria-label={`${setting.label}: ${channel.label}`}
                            className="data-[state=checked]:!bg-seafoam-500 data-[state=unchecked]:!bg-cocoa-100"
                            onCheckedChange={(checked) =>
                                onOverridesChange(setVisibilityOverride(overrides, setting.id, channel.key, checked))
                            }
                        />
                    </div>
                ))}
            </div>
            {setting.selection === 'periods' && (
                <SelectionRow
                    label="Choose time periods"
                    values={Object.values(ReportingPeriod)}
                    selectedValues={effectiveSettings.performance.trailingReturns.enabledPeriods}
                    labels={REPORTING_PERIOD_LABELS}
                    disabled={disabled}
                    onChange={(enabledPeriods) =>
                        onOverridesChange(setEnabledPeriodsOverride(overrides, enabledPeriods))
                    }
                />
            )}
            {setting.selection === 'statistics' && (
                <SelectionRow
                    label="Choose statistics"
                    values={Object.values(RiskStatistic)}
                    selectedValues={effectiveSettings.performance.riskStatistics.statistics}
                    labels={RISK_STATISTIC_LABELS}
                    disabled={disabled}
                    onChange={(statistics) => onOverridesChange(setRiskStatisticsOverride(overrides, statistics))}
                />
            )}
            {setting.selection && anyChannelVisible && selectedCount === 0 && (
                <p className="text-gold-600 border-cocoa-100 border-t px-7 py-3 text-sm">
                    This setting is visible, but nothing is selected — the section will render empty.
                </p>
            )}
        </div>
    );
};

const SelectionRow = <T extends string>({
    label,
    values,
    selectedValues,
    labels,
    disabled,
    onChange,
}: {
    label: string;
    values: T[];
    selectedValues: T[];
    labels: Record<T, string>;
    disabled?: boolean;
    onChange: (values: T[]) => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const selected = new Set(selectedValues);

    const toggleValue = (value: T) => {
        const nextValues = new Set(selectedValues);
        if (nextValues.has(value)) {
            nextValues.delete(value);
        } else {
            nextValues.add(value);
        }
        onChange(values.filter((candidate) => nextValues.has(candidate)));
    };

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger
                disabled={disabled}
                className="text-seafoam-600 hover:text-seafoam-700 border-cocoa-100 flex w-full cursor-pointer items-center gap-2 border-t px-7 py-3 text-left text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
                {isOpen ? <LuChevronDown className="size-4" /> : <LuChevronRight className="size-4" />}
                <span>{label}</span>
                <span className="text-cocoa-400 ml-2 font-normal">
                    {selectedValues.length === 0 ? 'None selected' : `${selectedValues.length} selected`}
                </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="border-cocoa-100 bg-cocoa-0/40 flex flex-wrap gap-3 border-t px-7 py-4">
                    {values.map((value) => (
                        <label
                            key={value}
                            className={cn(
                                'border-cocoa-100 text-cocoa-700 flex cursor-pointer items-center gap-2 rounded-full border bg-white px-3 py-2 text-sm',
                                disabled && 'cursor-not-allowed opacity-50',
                            )}
                        >
                            <Checkbox
                                checked={selected.has(value)}
                                disabled={disabled}
                                onCheckedChange={() => toggleValue(value)}
                            />
                            {labels[value]}
                        </label>
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};
